// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EventFormData } from "../CreateEventForm";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";

interface SponsorsTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
}

interface Sponsor {
  id: string;
  name: string;
  website_url?: string;
  logo_url?: string;
}

interface AttachedSponsor extends Sponsor {
  event_sponsor_id: string;
}

const SponsorsTab: React.FC<SponsorsTabProps> = ({ formData, updateFormData }) => {
  const { createOrgId, ready: orgScopeReady } = useAdminOrgScope();
  const [globalSponsors, setGlobalSponsors] = useState<Sponsor[]>([]);
  const [attachedSponsors, setAttachedSponsors] = useState<AttachedSponsor[]>([]);
  const [selectedSponsorIds, setSelectedSponsorIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Local state for inline form
  const [inlineFormData, setInlineFormData] = useState({
    name: "",
    website_url: "",
    logo_file: null as File | null
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchGlobalSponsors();
    if (formData.id) {
      fetchAttachedSponsors();
    }
  }, [formData.id]);

  const fetchGlobalSponsors = async () => {
    try {
      const { data, error } = await supabase
        .from('sponsors')
        .select('*')
        .order('name');

      if (error) throw error;
      setGlobalSponsors(data || []);
    } catch (error) {
      console.error('Error fetching global sponsors:', error);
      toast.error('Failed to load sponsors');
    } finally {
      // Set loading to false after global sponsors are loaded
      if (!formData.id) {
        setIsLoading(false);
      }
    }
  };

  const fetchAttachedSponsors = async () => {
    if (!formData.id) return;

    try {
      const { data, error } = await supabase
        .from('event_sponsors')
        .select(`
          id,
          sponsors (
            id,
            name,
            website_url,
            logo_url
          )
        `)
        .eq('event_id', formData.id);

      if (error) throw error;

      const attachedData = (data || []).map(item => {
        const sponsor = item.sponsors as any; // Type assertion for nested data
        return {
          event_sponsor_id: item.id,
          id: sponsor?.id || '',
          name: sponsor?.name || '',
          website_url: sponsor?.website_url || '',
          logo_url: sponsor?.logo_url || ''
        };
      });

      setAttachedSponsors(attachedData);
    } catch (error) {
      console.error('Error fetching attached sponsors:', error);
      toast.error('Failed to load attached sponsors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSponsorSelection = (sponsorId: string, checked: boolean) => {
    if (checked) {
      setSelectedSponsorIds(prev => [...prev, sponsorId]);
    } else {
      setSelectedSponsorIds(prev => prev.filter(id => id !== sponsorId));
    }
  };

  const attachSelectedSponsors = async () => {
    if (!formData.id || selectedSponsorIds.length === 0) {
      toast.error('Please select sponsors to attach');
      return;
    }

    try {
      const attachPromises = selectedSponsorIds.map(sponsorId =>
        supabase
          .from('event_sponsors')
          .insert({
            event_id: formData.id,
            sponsor_id: sponsorId
          })
      );

      await Promise.all(attachPromises);
      
      setSelectedSponsorIds([]);
      await fetchAttachedSponsors();
      toast.success(`Attached ${selectedSponsorIds.length} sponsor(s) to event`);
    } catch (error) {
      console.error('Error attaching sponsors:', error);
      toast.error('Failed to attach sponsors');
    }
  };

  const attachSingleSponsor = async (sponsorId: string) => {
    if (!formData.id) {
      toast.error('Event must be saved before attaching sponsors');
      return;
    }

    try {
      const { error } = await supabase
        .from('event_sponsors')
        .insert({
          event_id: formData.id,
          sponsor_id: sponsorId
        });

      if (error) throw error;

      await fetchAttachedSponsors();
      toast.success('Sponsor attached to event');
    } catch (error) {
      console.error('Error attaching sponsor:', error);
      toast.error('Failed to attach sponsor');
    }
  };

  const removeSponsor = async (eventSponsorId: string) => {
    try {
      const { error } = await supabase
        .from('event_sponsors')
        .delete()
        .eq('id', eventSponsorId);

      if (error) throw error;

      setAttachedSponsors(prev => 
        prev.filter(sponsor => sponsor.event_sponsor_id !== eventSponsorId)
      );
      toast.success('Sponsor removed from event');
    } catch (error) {
      console.error('Error removing sponsor:', error);
      toast.error('Failed to remove sponsor');
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setInlineFormData(prev => ({ ...prev, logo_file: file }));
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        toast.error('Please select an image file');
      }
    }
  };

  const uploadLogo = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `sponsors/sponsor-logo-${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('org-logos')
        .upload(fileName, file, { cacheControl: "3600", upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('org-logos')
        .getPublicUrl(uploadData.path);
      
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
      return null;
    }
  };

  const handleInlineFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inlineFormData.name.trim()) {
      toast.error('Sponsor name is required');
      return;
    }

    if (!orgScopeReady) {
      toast.error('Organization is still loading. Please try again in a moment.');
      return;
    }

    setIsSubmitting(true);

    try {
      let logoUrl = null;
      
      if (inlineFormData.logo_file) {
        logoUrl = await uploadLogo(inlineFormData.logo_file);
      }

      const { error } = await supabase
        .from('sponsors')
        .insert({
          name: inlineFormData.name,
          website_url: inlineFormData.website_url.trim()
            ? /^https?:\/\//i.test(inlineFormData.website_url.trim())
              ? inlineFormData.website_url.trim()
              : `https://${inlineFormData.website_url.trim()}`
            : null,
          logo_url: logoUrl,
          organization_id: createOrgId,
        });

      if (error) throw error;

      setInlineFormData({
        name: "",
        website_url: "",
        logo_file: null
      });
      setLogoPreview(null);
      setShowInlineForm(false);

      await fetchGlobalSponsors();
      toast.success('Sponsor created successfully');
    } catch (error) {
      console.error('Error creating sponsor:', error);
      toast.error('Failed to create sponsor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelInlineForm = () => {
    setInlineFormData({
      name: "",
      website_url: "",
      logo_file: null
    });
    setLogoPreview(null);
    setShowInlineForm(false);
  };

  const availableSponsors = globalSponsors.filter(sponsor => 
    !attachedSponsors.some(attached => attached.id === sponsor.id)
  );

  if (isLoading) {
    return <div>Loading sponsors...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Create New Sponsor */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Sponsor</CardTitle>
        </CardHeader>
        <CardContent>
          {!showInlineForm ? (
            <Button 
              type="button" 
              onClick={() => setShowInlineForm(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add New Sponsor
            </Button>
          ) : (
            <Card className="bg-gray-50">
              <CardContent className="pt-6">
                <form onSubmit={handleInlineFormSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="sponsor_name">Sponsor Name *</Label>
                    <Input
                      id="sponsor_name"
                      value={inlineFormData.name}
                      onChange={(e) => setInlineFormData(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      required
                      placeholder="Enter sponsor name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sponsor_website">Website URL</Label>
                    <Input
                      id="sponsor_website"
                      type="text"
                      inputMode="url"
                      value={inlineFormData.website_url}
                      onChange={(e) => setInlineFormData(prev => ({
                        ...prev,
                        website_url: e.target.value
                      }))}
                      placeholder="https://example.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sponsor_logo">Logo Upload</Label>
                    <div className="space-y-2">
                      {logoPreview && (
                        <div className="relative inline-block">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="w-20 h-20 object-contain border rounded"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 w-6 h-6 p-0"
                            onClick={() => {
                              setLogoPreview(null);
                              setInlineFormData(prev => ({ ...prev, logo_file: null }));
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      <Input
                        id="sponsor_logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      {isSubmitting ? 'Creating...' : 'Create Sponsor'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleCancelInlineForm}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Available Sponsors */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Available Sponsors</CardTitle>
            {selectedSponsorIds.length > 0 && (
              <Button type="button" onClick={attachSelectedSponsors}>
                Attach Selected ({selectedSponsorIds.length})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {availableSponsors.length > 0 ? (
            <div className="space-y-2">
              {availableSponsors.map((sponsor) => (
                <div key={sponsor.id} className="flex items-center space-x-3 p-2 border rounded">
                  <Checkbox
                    id={`available-sponsor-${sponsor.id}`}
                    checked={selectedSponsorIds.includes(sponsor.id)}
                    onCheckedChange={(checked) => 
                      handleSponsorSelection(sponsor.id, checked as boolean)
                    }
                  />
                  {sponsor.logo_url && (
                    <img
                      src={sponsor.logo_url}
                      alt={`${sponsor.name} logo`}
                      className="w-8 h-8 object-contain"
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-medium">{sponsor.name}</div>
                    {sponsor.website_url && (
                      <div className="text-sm text-gray-500">{sponsor.website_url}</div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => attachSingleSponsor(sponsor.id)}
                    disabled={!formData.id}
                  >
                    Attach
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No available sponsors to attach.</p>
          )}
        </CardContent>
      </Card>

      {/* Attached Sponsors */}
      <Card>
        <CardHeader>
          <CardTitle>Attached Sponsors</CardTitle>
        </CardHeader>
        <CardContent>
          {attachedSponsors.length > 0 ? (
            <div className="space-y-2">
              {attachedSponsors.map((sponsor) => (
                <div key={sponsor.event_sponsor_id} className="flex items-center space-x-3 p-2 border rounded bg-green-50">
                  {sponsor.logo_url && (
                    <img
                      src={sponsor.logo_url}
                      alt={`${sponsor.name} logo`}
                      className="w-8 h-8 object-contain"
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-medium">{sponsor.name}</div>
                    {sponsor.website_url && (
                      <div className="text-sm text-gray-500">{sponsor.website_url}</div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeSponsor(sponsor.event_sponsor_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No sponsors attached to this event.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SponsorsTab;
